import pdfMake from 'pdfmake/build/pdfmake'
import 'pdfmake/build/vfs_fonts'
import type { TDocumentDefinitions } from 'pdfmake/interfaces'
import { store } from '../../../app/store'
import type { PrescriberLocationProfile } from '../../../types/location'
import { get } from 'lodash'
import { format, parseISO } from 'date-fns'
import JsBarcode from 'jsbarcode'
import { PrescribedItem, Prescription } from '../../../types'
import paperPrescriptionBg from '../../../assets/paperPrescriptionBg.json'

export const openPdf = (docDefinition: TDocumentDefinitions) => {
  pdfMake.createPdf(docDefinition).open()
}

type PrescriptionViewData = {
  prescriberTitle: string
  prescriberFirstname: string
  prescriberLastname: string
  prescriberNumber: string
  prescriberPhoneNumber: string
  prescriberOtherPhoneNumber: string
  prescriberFax: string
  prescriberProviderNumber: string
  prescriberHPII: string
  clinicName: string
  clinicAddress: string
  clinicSuburb: string
  clinicPostcode: string
  clinicState: string
  clinicPhone: string
  patientFirstname: string
  patientLastname: string
  patientMedicareNumber: string
  patientMedicareIRN: string
  patientDvaNumber: string
  patientAddress: string
  patientSuburb: string
  patientState: string
  patientDob: string
  patientPostcode: string
  date: string
  concessionPensionNo: string
  pbsSafetyNetEntitlementCardHolder: boolean
  pbsConcessionCardHolder: boolean
  scriptId: string
  isPBS: boolean
  isRPBS: boolean
  ctgAnnotation: string
  brandSubstitutionNotAllowed: boolean
  erxBarcode: string
  prescribedItems: (PrescribedItem | undefined)[]
  sendViaEmail?: boolean
  sendViaSMS?: boolean
  printPaperToken?: boolean
  isPaperPrescription?: boolean
  recipientMobile?: string
  recipientEmail?: string
  prescriberEmail?: string
}

const transformAddress = (addressLine1: string, addressLine2?: string) => {
  return addressLine1 + (addressLine2 ? `, ${addressLine2}` : '')
}

const str = (value: string | null | undefined) => value ?? ''

export const getPrescriptionViewDataForNewScript = (
  location?: PrescriberLocationProfile | null
): PrescriptionViewData => {
  const state = store.getState()
  const currentClinic = state.clinic.currentClinic
  const useWorkLocationProfile = !!location?.address1

  const clinicAddressLine1 = useWorkLocationProfile
    ? get(location, 'address1', '')
    : get(currentClinic, 'address_1', '')
  const clinicAddressLine2 = useWorkLocationProfile
    ? get(location, 'address2', '')
    : get(currentClinic, 'address_2', '')
  const patientAddressLine1 = get(state, 'patientDetail.selectedPatient.address_1', '')
  const patientAddressLine2 = get(state, 'patientDetail.selectedPatient.address_2', '')

  return {
    prescriberTitle: get(state, 'prescriber.prescriber.title', ''),
    prescriberFirstname: get(state, 'prescriber.prescriber.givenNames', ''),
    prescriberLastname: get(state, 'prescriber.prescriber.familyName', ''),
    prescriberNumber: get(state, 'prescriber.prescriber.prescriberNumber', ''),
    prescriberPhoneNumber: useWorkLocationProfile
      ? get(location, 'phoneNumber', '')
      : get(state, 'prescriber.prescriber.phoneNumber', ''),
    prescriberOtherPhoneNumber: get(state, 'prescriber.prescriber.otherPhoneNumber', ''),
    prescriberFax: get(state, 'prescriber.prescriber.faxNumber', ''),
    prescriberProviderNumber:
      get(location, 'providerNumber') || get(state, 'prescriber.prescriber.providerNumber', ''),
    prescriberHPII: get(state, 'prescriber.prescriber.hpii', ''),
    clinicName: useWorkLocationProfile ? get(location, 'name', '') : get(currentClinic, 'name', ''),
    clinicAddress: transformAddress(str(clinicAddressLine1), str(clinicAddressLine2)),
    clinicSuburb: useWorkLocationProfile
      ? get(location, 'suburb', '')
      : get(currentClinic, 'suburb', ''),
    clinicPostcode: useWorkLocationProfile
      ? get(location, 'postcode', '')
      : get(currentClinic, 'postcode', ''),
    clinicState: useWorkLocationProfile
      ? get(location, 'state', '')
      : get(currentClinic, 'state', ''),
    clinicPhone: str(
      useWorkLocationProfile
        ? get(location, 'phoneNumber', '')
        : get(currentClinic, 'phone_number', '')
    ),
    prescriberEmail: get(location, 'email', '') || get(state, 'prescriber.prescriber.email', ''),
    patientFirstname: get(state, 'patientDetail.selectedPatient.given_names', ''),
    patientLastname: get(state, 'patientDetail.selectedPatient.family_name', ''),
    patientMedicareNumber: get(state, 'patientDetail.selectedPatient.medicare_no', ''),
    patientMedicareIRN: get(state, 'patientDetail.selectedPatient.medicare_irn', ''),
    patientDvaNumber: get(state, 'patientDetail.selectedPatient.dva_file_no', ''),
    patientAddress: transformAddress(patientAddressLine1, patientAddressLine2),
    patientSuburb: get(state, 'patientDetail.selectedPatient.suburb', ''),
    patientState: get(state, 'patientDetail.selectedPatient.state', ''),
    patientDob: get(state, 'patientDetail.selectedPatient.date_of_birth'),
    patientPostcode: get(state, 'patientDetail.selectedPatient.postcode', '').toString(),
    date: format(new Date(), 'dd/MM/yyyy'),
    concessionPensionNo: get(state, 'patientDetail.selectedPatient.concession_pension_no', ''),
    pbsSafetyNetEntitlementCardHolder: get(
      state,
      'patient.patient.pbs_safety_net_entitlement_card_holder',
      false
    ), // Always false for now
    pbsConcessionCardHolder: get(state, 'patient.patient.pbs_concession_card_holder', false), // Always false for now
    scriptId: get(state, 'newScript.prescribedItemList[0].prescriberScriptNumber', ''),
    isPBS: get(state, 'newScript.prescribedItemList[0].isPBS', false),
    isRPBS: get(state, 'newScript.prescribedItemList[0].isRPBS', false),
    ctgAnnotation: get(state, 'newScript.prescribedItemList[0].ctgAnnotation', ''),
    brandSubstitutionNotAllowed: get(
      state,
      'newScript.prescribedItemList[0].brandSubstitutionNotAllowed',
      false
    ),
    erxBarcode: '',
    prescribedItems: get(state, 'newScript.prescribedItemList', []),
    sendViaEmail: get(state, 'newScript.sendViaEmail', false),
    sendViaSMS: get(state, 'newScript.sendViaSMS', false),
    printPaperToken: get(state, 'newScript.printPaperToken', false),
    isPaperPrescription: get(state, 'newScript.isPaperPrescription', false),
    recipientMobile: get(state, 'newScript.recipientMobile', ''),
    recipientEmail: get(state, 'newScript.recipientEmail', ''),
  }
}

export const getPrescriptionViewData = (prescription: Prescription): PrescriptionViewData => {
  const state = store.getState()
  const currentClinic = state.clinic.currentClinic
  const embeddedLocation = prescription.issueSnapshot.location
  const usePrescriptionLocation = !!embeddedLocation?.address1
  const patient = prescription.issueSnapshot.patient
  const prescriber = prescription.issueSnapshot.prescriber

  const clinicAddressLine1 = usePrescriptionLocation
    ? get(embeddedLocation, 'address1', '')
    : get(currentClinic, 'address_1', '')
  const clinicAddressLine2 = usePrescriptionLocation
    ? get(embeddedLocation, 'address2', '')
    : get(currentClinic, 'address_2', '')
  const patientAddressLine1 = get(patient, 'address1', '')
  const patientAddressLine2 = get(patient, 'address2', '')
  const createdDate = new Date(get(prescription, 'createdDate', new Date()))
  return {
    prescriberTitle: get(prescriber, 'title', ''),
    prescriberFirstname: get(prescriber, 'givenNames', ''),
    prescriberLastname: get(prescriber, 'familyName', ''),
    prescriberNumber: get(prescriber, 'prescriberNumber', ''),
    prescriberPhoneNumber: get(prescriber, 'phoneNumber', ''),
    prescriberOtherPhoneNumber: get(prescriber, 'otherPhoneNumber', ''),
    prescriberFax: get(prescriber, 'faxNumber', ''),
    prescriberProviderNumber: get(prescriber, 'providerNumber', ''),
    prescriberHPII: get(prescriber, 'hpii', ''),
    clinicName: usePrescriptionLocation
      ? get(embeddedLocation, 'name', '')
      : get(currentClinic, 'name', ''),
    clinicAddress: transformAddress(str(clinicAddressLine1), str(clinicAddressLine2)),
    clinicSuburb: usePrescriptionLocation
      ? get(embeddedLocation, 'suburb', '') ?? ''
      : get(currentClinic, 'suburb', ''),
    clinicPostcode: usePrescriptionLocation
      ? get(embeddedLocation, 'postcode', '') ?? ''
      : get(currentClinic, 'postcode', ''),
    clinicState: usePrescriptionLocation
      ? get(embeddedLocation, 'state', '') ?? ''
      : get(currentClinic, 'state', ''),
    clinicPhone: str(
      usePrescriptionLocation
        ? get(embeddedLocation, 'phoneNumber', '')
        : get(currentClinic, 'phone_number', '')
    ),
    patientFirstname: get(patient, 'givenNames', ''),
    patientLastname: get(patient, 'familyName', ''),
    patientMedicareNumber: get(patient, 'medicareNo', ''),
    patientMedicareIRN: get(patient, 'medicareIrn', ''),
    patientDvaNumber: get(patient, 'dvaFileNo', ''),
    patientAddress: transformAddress(patientAddressLine1, patientAddressLine2),
    patientSuburb: get(patient, 'suburb', ''),
    patientState: get(patient, 'state', ''),
    patientDob: get(patient, 'dateOfBirth'),
    patientPostcode: get(patient, 'postcode', '').toString(),
    date: format(createdDate, 'dd/MM/yyyy'),
    concessionPensionNo: get(patient, 'concessionPensionNo', ''),
    pbsSafetyNetEntitlementCardHolder: get(
      patient,
      'pbs_safety_net_entitlement_card_holder',
      false
    ), // TBC: we don't have this field
    pbsConcessionCardHolder: get(patient, 'pbsConcessionCardHolder', false), // TBC: we don't have this field
    scriptId: get(prescription, 'prescribedItems[0].prescriberScriptNumber'),
    isPBS: get(prescription, 'prescribedItems[0].isPBS', false),
    isRPBS: get(prescription, 'prescribedItems[0].isRPBS', false),
    ctgAnnotation: get(prescription, 'prescribedItems[0].ctgAnnotation', ''),
    brandSubstitutionNotAllowed: get(
      prescription,
      'prescribedItems[0].brandSubstitutionNotAllowed',
      false
    ),
    erxBarcode: '',
    prescribedItems: get(prescription, 'prescribedItems', []),
  }
}

export const getDrugStack = (prescriptionViewData: PrescriptionViewData) => {
  const prescribedItems = get(prescriptionViewData, 'prescribedItems', [])
  if (prescribedItems.length > 0) {
    const phoneApprovalAuthorityNumber = prescribedItems[0]?.phoneApprovalAuthorityNumber
    return [
      ...prescribedItems.flatMap((d: any) => {
        return [
          {
            text: `${d.displayName}`,
            bold: true,
          },
          d.patientInstructions ? `Directions: ${d.patientInstructions}` : '',
          `Quantity: ${d.quantity}, Repeats: ${d.numberOfRepeatsAuthorised}.${
            d.regulation24 ? ' (Reg. 49),' : ''
          } Interval: ${d.repeatIntervals || ''}`,
          d.doctorsNotes ? `Notes: ${d.doctorsNotes}` : '',
          '-----------------------------------------------------------------------------------------',
        ]
      }),
      '',
      '',
      {
        text: `Signed:  _______________  (${prescriptionViewData.prescriberTitle} ${prescriptionViewData.prescriberFirstname} ${prescriptionViewData.prescriberLastname})`,
        bold: true,
      },
      ...(phoneApprovalAuthorityNumber
        ? [
            '',
            {
              text: `${prescriptionViewData.prescriberTitle} ${prescriptionViewData.prescriberFirstname} ${prescriptionViewData.prescriberLastname}`,
              bold: true,
            },
            `Provider No. ${prescriptionViewData.prescriberProviderNumber}`,
            `Approval Number: ${
              prescriptionViewData.prescribedItems[0]?.phoneApprovalAuthorityNumber ?? ''
            }`,
            '',
            `Authorised Delegate: _______________________`,
          ]
        : []),
    ]
  }
  return []
}

const getBarcodeImage = (scid: string): string => {
  const canvas = document.createElement('canvas')
  JsBarcode(canvas, scid, { format: 'CODE128A' })
  return canvas.toDataURL('image/png')
}

export const getAuthorityInfoStack = (name: string, prescription: Prescription) => {
  const data = getPrescriptionViewData(prescription)
  const phoneApprovalAuthorityNumber = get(
    data,
    `prescribedItems[0].phoneApprovalAuthorityNumber`,
    ''
  )
  return phoneApprovalAuthorityNumber
    ? [
        {
          text: `${name} - Date: ${data.date}`,
          bold: true,
        },
        '-----------------------------------------------------------------------------------------',
        `Authority Form No.: ${data.scriptId}`,
        `${data.prescriberTitle} ${data.prescriberFirstname} ${data.prescriberLastname}`,
        `${data.clinicAddress}`,
        `${data.clinicSuburb} ${data.clinicState} ${data.clinicPostcode}`,
        `Prescriber No.: ${data.prescriberNumber}                     Phone: ${data.prescriberPhoneNumber}`,
        '',
        {
          text: `${data.patientFirstname} ${data.patientLastname}`,
          bold: true,
        },
        `${data.patientAddress}`,
        `${data.patientSuburb} ${data.patientState} ${data.patientPostcode}`,
        '-----------------------------------------------------------------------------------------',
        `${get(data, 'prescribedItems[0].genericName', '')}`,
        `${get(data, 'prescribedItems[0].patientInstructions', '')}`,
        `Quantity: ${get(data, 'prescribedItems[0].quantity', '')} Repeats: ${get(
          data,
          'prescribedItems[0].numberOfRepeatsAuthorised',
          ''
        )}`,
        '-----------------------------------------------------------------------------------------',
        `Approval No.: ${phoneApprovalAuthorityNumber}`,
        `Previous Authority: No`,
        {
          text: 'Indication for Authority',
          bold: true,
        },
        `${get(data, 'prescribedItems[0].reasonForPrescribing', '')}`,
      ]
    : []
}

export const printPaperPrescription = (prescriptions: Prescription[] | Prescription) => {
  const toBePrintedPrescriptions = Array.isArray(prescriptions) ? prescriptions : [prescriptions]
  const pages = toBePrintedPrescriptions.map((prescription, index) => {
    const data = getPrescriptionViewData(prescription)
    const barcodeImage = getBarcodeImage(prescription.scid)

    const page = [
      {
        columns: [
          {
            margin: [16, -2],
            stack: [
              `${data.prescriberTitle} ${data.prescriberFirstname} ${data.prescriberLastname}`,
              data.clinicAddress,
              `${data.clinicSuburb} ${data.clinicPostcode}`,
              {
                columns: [
                  {
                    text: `Phone: ${data.clinicPhone}`,
                  },
                  {
                    text: `Fax: ${data.prescriberFax}`,
                    margin: [-50, 0, 0, 0],
                  },
                ],
              },
              {
                text: `Other Phone: ${data.prescriberOtherPhoneNumber || '-'}`,
              },
              {
                text: data.prescriberNumber,
                margin: [50, 1, 0, 0],
              },
              {
                text:
                  data.patientMedicareNumber && data.patientMedicareIRN
                    ? `${data.patientMedicareNumber}-${data.patientMedicareIRN}`
                    : '',
                margin: [83, 11, 0, 0],
              },
              {
                text: data.patientDvaNumber || data.concessionPensionNo,
                margin: [70, 22, 0, 0],
              },
              {
                columns: [
                  {
                    text: data.pbsSafetyNetEntitlementCardHolder ? 'X' : '',
                    margin: [54, 25, 0, 0],
                  },
                  {
                    text: data.pbsConcessionCardHolder ? 'X' : '',
                    margin: [37, 25, 0, 0],
                  },
                ],
              },
              {
                margin: [50, 10, 0, 0],
                stack: [
                  `${data.patientFirstname} ${data.patientLastname}                                     ${data.ctgAnnotation}`,
                  data.patientAddress,
                  `${data.patientSuburb} ${data.patientPostcode}`,
                  `DOB: ${format(parseISO(data.patientDob), 'dd/MM/yyyy')}`,
                ],
              },
              {
                margin: [20, 4, 0, 0],
                columns: [data.date, `Script ID: ${data.scriptId}`],
              },
              {
                columns: [
                  {
                    text: data.isPBS ? 'X' : '',
                    margin: [20, 3, 0, 0],
                  },
                  {
                    text: data.isRPBS ? 'X' : '',
                    margin: [-15, 3, 0, 0],
                  },
                  {
                    text: data.brandSubstitutionNotAllowed ? 'X' : '',
                    margin: [-64, 3, 0, 0],
                  },
                ],
              },
              {
                margin: [54, 20, 0, 0],
                fontSize: 8,
                stack: getDrugStack(data),
              },
              {
                columns: [
                  {
                    width: 30,
                    absolutePosition: { x: 35, y: 490 },
                    image:
                      'data:image/bmp;base64,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',
                  },
                  {
                    width: 200,
                    height: 50,
                    absolutePosition: { x: 68, y: 475 },
                    image: barcodeImage,
                  },
                ],
              },
              {
                fontSize: 8,
                margin: [0, 180, 0, 0],
                stack: getAuthorityInfoStack("Doctor's Copy", prescription),
              },
            ],
          },
          {
            margin: [8, -2],
            stack: [
              `${data.prescriberTitle} ${data.prescriberFirstname} ${data.prescriberLastname}`,
              data.clinicAddress,
              `${data.clinicSuburb} ${data.clinicPostcode}`,
              {
                columns: [
                  {
                    text: `Phone: ${data.clinicPhone}`,
                  },
                  {
                    text: `Fax: ${data.prescriberFax}`,
                    margin: [-50, 0, 0, 0],
                  },
                ],
              },
              {
                text: `Other Phone: ${data.prescriberOtherPhoneNumber || '-'}`,
              },
              {
                text: data.prescriberNumber,
                margin: [50, 1, 0, 0],
              },
              {
                text:
                  data.patientMedicareNumber && data.patientMedicareIRN
                    ? `${data.patientMedicareNumber}-${data.patientMedicareIRN}`
                    : '',
                margin: [83, 11, 0, 0],
              },
              {
                text: data.patientDvaNumber || data.concessionPensionNo || '',
                margin: [58, 22, 0, 0],
              },
              {
                columns: [
                  {
                    text: data.pbsSafetyNetEntitlementCardHolder ? 'X' : '',
                    margin: [80, 25, 0, 0],
                  },
                  {
                    text: data.pbsConcessionCardHolder ? 'X' : '',
                    margin: [37, 25, 0, 0],
                  },
                ],
              },
              {
                margin: [55, 10, 0, 0],
                stack: [
                  `${data.patientFirstname} ${data.patientLastname}                                     ${data.ctgAnnotation}`,
                  data.patientAddress,
                  `${data.patientSuburb} ${data.patientPostcode}`,
                  `DOB: ${format(parseISO(data.patientDob), 'dd/MM/yyyy')}`,
                ],
              },
              {
                margin: [20, 4, 0, 0],
                columns: [data.date, `Script ID: ${data.scriptId}`],
              },
              {
                columns: [
                  {
                    text: data.isPBS ? 'X' : '',
                    margin: [20, 3, 0, 0],
                  },
                  {
                    text: data.isRPBS ? 'X' : '',
                    margin: [-18, 3, 0, 0],
                  },
                  {
                    text: data.brandSubstitutionNotAllowed ? 'X' : '',
                    margin: [-66, 3, 0, 0],
                  },
                ],
              },
              {
                fontSize: 8,
                margin: [64, 20, 8, 0],
                stack: getDrugStack(data),
              },
              {
                fontSize: 8,
                margin: [0, 180, 0, 0],
                stack: getAuthorityInfoStack('HIC Copy', prescription),
              },
            ],
          },
        ],
        columnGap: 0,
      } as any,
    ]

    if (index < toBePrintedPrescriptions.length - 1) {
      page.push({
        text: '',
        pageBreak: 'after',
      })
    }
    return page
  })

  const dd: TDocumentDefinitions = {
    defaultStyle: {
      fontSize: 9,
    },
    pageSize: 'A4',
    pageMargins: [0, 16],
    content: pages,
  }

  if (process.env.NODE_ENV === 'development') {
    dd.background = {
      image: paperPrescriptionBg.dataUri,
      width: 595,
      height: 842,
    }
  }

  openPdf(dd)
}
